import { Dimmer, Form, List, Loader, SectionItem } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { APPLICATIONS_SOURCES } from '@/constants';
import { RegieContext } from '@/contexts';
import { useClipboard, useProduitsJvs } from '@/hooks';
import { postLogicielFacturier, putLogicielFacturier } from '@/routes';
import type { LogicielFacturier, RegieType } from '@/types';
import { getApplicationSourcePriority, getRegieCodeFromJvs, isHandledApplication } from '@/utils';
import { useRegieStore } from '@/zustand';
import ApplicationsSourcesListItem from './ApplicationsSourcesListItem';

const ApplicationsSources = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  // State interne pour maj les régies après les modifs de logiciels, c'est plus simple
  const [internalRegies, setInternalRegies] = React.useState<RegieType[]>();
  const regieAutonome = useRegieStore((state) => state.regie?.autonome);

  const [produits, loadingProduits] = useProduitsJvs();
  const { copyToClipboard } = useClipboard();

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = React.useMemo(
    () => _.find(internalRegies, { identifiant: selectedRegie }),
    [internalRegies, selectedRegie],
  );
  const logicielsRegie = React.useMemo(() => regie?.logicielsFacturier, [regie]);

  const orderedProduits = React.useMemo(() => _.orderBy(produits, 'nom'), [produits]);

  const produitsAvecLogicielFacturier = React.useMemo(
    () =>
      _.map(orderedProduits, (produit) => ({
        ...produit,
        logicielFacturier: _.find(logicielsRegie, { identifiantProduit: produit.identifiant }),
      })),
    [orderedProduits, logicielsRegie],
  );

  const updateLogicielFacturier = React.useCallback(
    (logiciel: LogicielFacturier, withToast = false) => {
      setLoading(true);

      request
        .put(putLogicielFacturier(logiciel.identifiant), logiciel)
        .then((updated: LogicielFacturier) => {
          // On remplace le logiciel facturier dans la régie
          setInternalRegies((prev) =>
            _.map(prev, (reg) => {
              if (reg.identifiant === selectedRegie) {
                return {
                  ...reg,
                  logicielsFacturier: _.map(reg.logicielsFacturier, (log) => {
                    if (log.identifiant === updated.identifiant) {
                      return updated;
                    }

                    return log;
                  }),
                };
              }

              return reg;
            }),
          );

          if (withToast) {
            if (updated.actif) {
              toast(
                "La liaison avec cette application a été réactivée et l'identifiant a été copié dans le presse-papier",
                { type: 'success' },
              );
              copyToClipboard(updated.cleLiaison);
            } else {
              toast('La liaison avec cette application a été désactivée', { type: 'warning' });
            }
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [copyToClipboard, selectedRegie],
  );

  const createLogicielFacturier = React.useCallback(
    (logiciel: Omit<LogicielFacturier, 'identifiant'>) => {
      request
        .post(postLogicielFacturier(), { ...logiciel, actif: true })
        .then((created: LogicielFacturier) => {
          // maj des logiciels facturier de la régie dans internalRegies
          setInternalRegies((prev) =>
            _.map(prev, (reg) => {
              if (reg.identifiant === selectedRegie) {
                return {
                  ...reg,
                  logicielsFacturier: [...reg.logicielsFacturier, created],
                };
              }

              return reg;
            }),
          );

          copyToClipboard(created.cleLiaison, 'Identifiant généré et copié dans le presse-papier');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [copyToClipboard, selectedRegie],
  );

  const handleUuidGenerate = React.useCallback(
    (produit) => () => {
      if (!_.isNil(produit.logicielFacturier)) {
        // Si on passe là, c'est qu'on a un logiciel facturier sans clé de liaison, pas normal
        // Mais on gère au cas où pour la QA, pour créer la clé
        updateLogicielFacturier(produit.logicielFacturier);
      } else {
        const codeRegie = getRegieCodeFromJvs(produit.code);

        const logicielToCreate: Omit<LogicielFacturier, 'identifiant'> = {
          identifiantProduit: produit.identifiant,
          code: codeRegie,
          // nom que nous on donne dans la régie
          nom: APPLICATIONS_SOURCES.APPLICATIONS_REGIE_LISTE[codeRegie],
        };

        createLogicielFacturier(logicielToCreate);
      }
    },
    [createLogicielFacturier, updateLogicielFacturier],
  );

  const handlePlugClick = React.useCallback(
    (logiciel: LogicielFacturier) => () => {
      updateLogicielFacturier({ ...logiciel, actif: !logiciel?.actif }, true);
    },
    [updateLogicielFacturier],
  );

  React.useEffect(() => {
    setInternalRegies(regies);
  }, [regies]);

  const handledProduits = React.useMemo(
    () => _.filter(produitsAvecLogicielFacturier, ({ code }) => code && isHandledApplication(code)),
    [produitsAvecLogicielFacturier],
  );

  // Plusieurs codes JVS peuvent pointer vers le même code régie (ex : 360_ENF, HOL_ENF4,
  // HOL_ENF3 → 'ENF'). On ne garde qu'un produit par code régie, en conservant le plus
  // prioritaire (cf. getApplicationSourcePriority), puis on retrie par nom pour l'affichage.
  // Si la régie est autonome, on ne montre que MFO.
  const handledProduitsFiltered = React.useMemo(() => {
    if (regieAutonome) {
      return _.filter(handledProduits, { code: APPLICATIONS_SOURCES.CODE_MFO });
    }

    const byPriority = _.sortBy(handledProduits, ({ code }) => getApplicationSourcePriority(code));
    const deduped = _.uniqBy(byPriority, ({ code }) => getRegieCodeFromJvs(code));

    return _.orderBy(deduped, 'nom');
  }, [handledProduits, regieAutonome]);

  return (
    <SectionItem title="Applications Groupe JVS" single compact>
      {loadingProduits || _.isNil(regie) ? (
        <List>
          <List.Item style={{ height: 60 }}>
            <Dimmer active inverted>
              <Loader>Chargement des applications du Groupe JVS</Loader>
            </Dimmer>
          </List.Item>
        </List>
      ) : _.isEmpty(handledProduitsFiltered) ? (
        <List>
          <List.Item style={{ padding: '1em', textAlign: 'center', color: 'grey' }}>
            Aucune application disponible
          </List.Item>
        </List>
      ) : (
        <List as={Form} divided>
          {_.map(handledProduitsFiltered, (produit) => (
            <ApplicationsSourcesListItem
              key={`produits-${produit.identifiant}`}
              produit={produit}
              loading={loading}
              onUuidGenerate={handleUuidGenerate(produit)}
              onPlugClick={handlePlugClick(produit.logicielFacturier)}
              onCopyToClipboard={() =>
                copyToClipboard(
                  produit.logicielFacturier?.cleLiaison,
                  'Identifiant copié dans le presse-papier',
                )
              }
            />
          ))}
        </List>
      )}
    </SectionItem>
  );
};

export default ApplicationsSources;
